import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { groupBy, flatten } from 'lodash';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { Place, ExpandMore } from '@mui/icons-material';
import { Check, Block } from '@mui/icons-material';
import TypeToSearch from '../../components/utils/TypeToSearch';
import GeotargetSection from '../../components/placement/GeotargetSection';
import MultiGeotargets from '../../components/placement/MultiGeotargets';
import { queryGeotargets } from '../../actions/geotargetsActions';


class Geotargets extends Component {
  constructor(props) {
    super(props);
    this.state = {
      queryString: '',
      errors: [],
      type: '',
    };
  }

  handleTypeChange = (evt) => {
    this.setState({ type: evt.target.value });
  }

  validate = (selected, exclude) => {
    const isParent = this.isParent(selected, exclude);
    const isChild = this.isChild(selected, exclude);
    const existing = this.exists(selected, exclude);
    return isParent || isChild || existing;
  }

  isParent = (selected, exclude) => {
    const { parents } = this.props;
    if (parents.has(String(selected.id))) {
      return { [selected.id]: `Areas within ${selected.name} are already targeted` };
    }
    return false;
  }

  isChild = (selected, exclude) => {
    const { geotargets } = this.props;
    const existing = new Set(geotargets.map(g => String(g.id)));
    return (selected.parentIds.split(',')
      .some(p => existing.has(p)) && !exclude) ?
      { [selected.id]: `Location ${selected.name} is within an area that is already targeted` } :
      false;
  }

  exists = selected => {
    const { geotargets } = this.props;
    const existing = geotargets.find(g => selected.id === g.id);
    if (existing) {
      return { [selected.id]: `Location ${selected.name} already exists` };
    }
    return false;
  }

  resetErrors = () => this.setState({ errors: [] });

  handleSelect = selected => this.addGeos([selected]);

  // Handles click on the secondary action buttons allowing to include or exclude geotargets
  // tag is either 'include' or 'exclude'
  handleGeotargetAction = (tag, geo) => {
    this.addGeos([geo], tag === 'exclude');
  }

  addGeos = (selected, exclude) => {
    const geotargets = flatten(selected.map(s => s.type === 'Geogroup' ? s.geotargets : s))
    const groups = selected.filter(s => s.type === 'Geogroup');
    const errors = [];
    geotargets.forEach(s => {
      const err = this.validate(s, exclude);
      if (err) {
        errors.push(err);
      }
    });
    if (errors.length > 0) {
      this.setState({ errors });
      if (groups.length > 0) {
        return; // do not add incomplete geogroup
      }
    } else {
      this.setState({ errors: [] });
    }
    let failed = errors.map(Object.keys);
    failed = [].concat(...failed);
    const valid = geotargets.filter(s => !failed.includes(s.id.toString()));
    const combinedGeotargets = [...this.props.geotargets, ...valid];
    const combinedGeogroups = [...this.props.geogroups, ...groups];
    const combinedExcludedGeotargets =  [...new Set([...this.props.excludedGeotargetIds, ...(exclude ? valid.map(g => g.id) : [])])];
    this.cleanInconsistentGeos(combinedGeotargets, combinedGeogroups, combinedExcludedGeotargets);
  }

  addMultiple = geos => {
    const { dispatch } = this.props;
    const { type } = this.state;
    const cleanNames = geos.split(',').map(g => g.trim());
    const names = [...new Set(cleanNames)]; // Set removes duplicates
    if (!names || names.length === 0) {
      return;
    }
    const query = names.map(n => `name=${n}`).join('&');
    dispatch(queryGeotargets(query))
      .then(() => {
        const typeFilteredGeos = type ?
          this.props.cache[query].filter(geo => geo.type === type) :
          this.props.cache[query];
        this.addGeos(typeFilteredGeos);
    });
  }

  deleteGeotarget = geo => () => {
    const { geotargets, geogroups, excludedGeotargetIds } = this.props;
    console.log('delete', excludedGeotargetIds);
    let egts = '';
    try {
      egts = JSON.parse(excludedGeotargetIds)
    } catch(e){
      console.log('error parsing excluded geos');
    }
    const remaining = geotargets.filter(g => g.id !== geo.id);
    let remainingExclusions = [];
    if (egts && egts.length > 0) {
      remainingExclusions = egts.filter(id => id !== geo.id);
    }
    this.cleanInconsistentGeos(remaining, geogroups, remainingExclusions);
  }

  deleteGeogroup = deletedGroup => () => {
    const { geotargets,geogroups } = this.props;
    const remainingGroups = geogroups.filter(g => g.id !== deletedGroup.id);
    const remainingTargets = geotargets.filter(t => !deletedGroup.geotargets.find(gt => gt.id === t.id))
    this.cleanInconsistentGeos(remainingTargets, remainingGroups);
  }

  cleanInconsistentGeos = (geotargets, geogroups, excludedGeotargetIds) => {
    const { onChange } = this.props;
    // Remove incomplete geogroups.This can happen when:
    // 1. geogroup added
    // 2. all geotarget members of the geogroup are automatically added
    // 3. User deletes one of the group members, then the group is considered incomplete
    geogroups = geogroups.filter(group => {
      for (let groupMember of group.geotargets) {
        if (!geotargets.some(t => t.id === groupMember.id)) {
          return false;
        }
      }
      return true;
    });
    // Remove geo exclusions whose parents are no longer included
    const inc = geotargets.filter(g => !excludedGeotargetIds.includes(g.id))
    const exc = geotargets.filter(g => excludedGeotargetIds.includes(g.id))
    const incIds = inc.map(g => g.id);
    const invalidExclusions = exc.filter(e => !e.parentIds.split(',').map(Number).some(p => incIds.includes(p))).map(e => e.id);
    excludedGeotargetIds = excludedGeotargetIds.filter(id => !invalidExclusions.includes(id));
    geotargets = geotargets.filter(g => !invalidExclusions.includes(g.id));
    const evt = {target: { name: 'Geotargets', value: { geotargets, geogroups, excludedGeotargetIds } }};
    onChange(evt);
  }

  query = evt => {
    const { cache, dispatch } = this.props;
    const query = `name=${evt.target.value}&useWildcard=true&geogroups=true`;
    if (!cache || !Object.prototype.hasOwnProperty.call(cache, query)) {
      dispatch(queryGeotargets(query));
    }
    this.setState({ queryString: query });
  }

  getSuggestions = query => {
    const { cache, geotargets, excludedGeotargetIds } = this.props;
    if (!cache) return[];
    if (!Object.prototype.hasOwnProperty.call(cache, query)) return[];;
    const ids = geotargets.map(g => g.id);
    const includedGeos = geotargets.filter(g => !excludedGeotargetIds.includes(g.id));
    const excludedGeos = geotargets.filter(g => excludedGeotargetIds.includes(g.id));
    const incIds = includedGeos.map(g => g.id);
    const incPids = flatten(includedGeos.map(g => g.parentIds.split(',').map(Number)));
    const excPids = flatten(excludedGeos.map(g => g.parentIds.split(',').map(Number)));
    const allPids = [...incPids, ...excPids];
    return cache[query].map(candidate => {
      let allowInclude = false;
      let allowExclude = false;
      // we can't include or exclude something that is already included or excluded
      if (!ids.includes(candidate.id)) {
        const cpids = candidate.parentIds.split(',').map(Number)
        // we can't include or exclude a region that encloses something that is already included or excluded
        if (!allPids.includes(candidate.id)) {
          // we can include anything that is not already included or excluded
          allowInclude = !cpids.some(p => ids.includes(p));
          // we can only exclude from included regions
          allowExclude = cpids.some(p => incIds.includes(p))
        }
      }
      return {  ...candidate, allowInclude, allowExclude };
    }).filter(g => g.allowInclude || g.allowExclude);
  }

  getGeoSections = () => {
    let sections = [];
    const geogroups = this.props.geogroups;
    if (geogroups.length > 0) {
      sections.push(
          <GeotargetSection
            type="Geogroups"
            geotargets={geogroups}
            onDelete={this.deleteGeogroup}
          />
      );
    }
    const { excludedGeotargetIds } = this.props;
    const geotargets = groupBy(this.props.geotargets, g => g.type);
    sections = sections.concat(Object.keys(geotargets)
      .map(g => (
        <GeotargetSection
          key={g}
          type={g}
          geotargets={geotargets[g]}
          excludedGeotargetIds={excludedGeotargetIds}
          onDelete={this.deleteGeotarget}
        />
      ))
    );
    return sections;
  }

  getAllowedGeotargetActions(geotarget) {
    let secondary = [];
    let disablePrimary = false;
    if (geotarget.allowInclude) {
      secondary.push({primary: true, tag: "include",icon: (<Check />), label: 'Include'});
    } else {
      disablePrimary = true;
    }
    if (geotarget.allowExclude) {
      secondary.push({primary: false, tag: "exclude",icon: (<Block />), label: 'Exclude'});
    }
    return  { secondary, disablePrimary };
  }

  render() {
    const { geotargets, classes, geoGlobal } = this.props;
    if (geoGlobal) {
      return null;
    }
    let errors = this.state.errors.map(Object.values);
    errors = [].concat(...errors);

    return (
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Place className={classes} />
          <Grid item xs={2}>
            <Typography variant="subtitle1">
              Geotargets ({geotargets ? geotargets.length : 0})
            </Typography>
          </Grid>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <TypeToSearch
                name="Geotargets"
                label="Search Geotargets"
                placeholder="Type to search"
                suggestions={this.getSuggestions(this.state.queryString)}
                primary="name"
                secondary={(suggestion) => `${suggestion.type} - ${suggestion.canonicalName}`}
                onChange={this.query}
                onSelect={this.handleSelect}
                onGetActions={this.getAllowedGeotargetActions}
                onSecondaryAction={this.handleGeotargetAction}
              />
              <Dialog
                open={errors.length > 0}
                onClose={this.resetErrors}
                aria-labelledby="dialog-title"
                aria-describedby="dialog-message"
              >
                <DialogTitle id="dialog-title">Errors adding geotargets</DialogTitle>
                <DialogContent id="dialog-message">
                  {
                    errors.map((e)=> <DialogContentText key={e.key}>{e}</DialogContentText>)
                  }
                  { errors.length > 1 && (
                  <Typography style={{
                      marginTop: 50,
                      fontWeight: 900
                    }}
                  >
                      Note: Valid geotargets will be added automatically
                  </Typography>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button onClick={this.resetErrors} color="primary">OK</Button>
                </DialogActions>
              </Dialog>
              <MultiGeotargets onAdd={this.addMultiple} onChangeType={this.handleTypeChange} type={this.state.type} />
            </Grid>

            <Grid item xs={6}>
              {this.getGeoSections()}
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    )
  }
}

Geotargets.defaultProps = {
  geotargets: []
}

Geotargets.propTypes = {
  classes: PropTypes.string.isRequired,
  geotargets: PropTypes.arrayOf(PropTypes.object)
}

const mapStateToProps = state => {
  const { geotargetRequest } = state;

  return {
    cache: geotargetRequest.cache,
    isFetching: geotargetRequest.isFetching
  }
}

export default connect(mapStateToProps)(Geotargets);
